/** AppShell — persistent left sidebar + top bar for every /app/* route.
 *
 *  Blueprint Part 4: role-aware navigation grouped Station / Agency / Account,
 *  live national pulse, station switcher, ⌘K hint, avatar menu, mobile drawer.
 *  Uses the design system primitives so it stays cohesive.
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { Outlet, NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Waveform, Pill } from "@/design";
import { color, type, radius, space, elevation, transition, font } from "@/design/tokens";
import { api } from "@/lib/api";

const SHELL_BG = color.navy900;
const SHELL_SURFACE = color.navy800;
const SHELL_LINE = color.line;
const SHELL_TEXT = "#fff";
const SHELL_MUTED = color.slate400;

const SIDEBAR_W = 244;

// ── Nav definition — keyed groups, each item has icon + label + path ─────
// For Batch 3 (sitemap migration), only items that route to fully working
// pages are shown. The deeper Station-tab items (audience, rate-card,
// schedule, ad-log, reports, widget) live INSIDE the Dashboard for now and
// get promoted to first-class routes when StationDash is split in Batch 5.
const NAV = {
  station: {
    label: "Station",
    accent: color.audience.station,
    items: [
      { id: "dashboard", label: "Dashboard", path: "/app/dashboard", icon: "📡" },
      { id: "rate-card", label: "Rate Card", path: "/app/rate-card", icon: "💰" },
      { id: "schedule",  label: "Schedule",  path: "/app/schedule",  icon: "📅" },
      { id: "profile",   label: "Profile",   path: "/app/profile",   icon: "🏷" },
      { id: "ad-log",    label: "Ad Log",    path: "/app/ad-log",    icon: "📋" },
      { id: "reports",   label: "Reports",   path: "/app/reports",   icon: "📑", phase2: true },
    ],
  },
  agency: {
    label: "Agency / Brand",
    accent: color.audience.agency,
    items: [
      { id: "planning",     label: "Planning",     path: "/app/planning",     icon: "🎯" },
      { id: "adverify",     label: "AdVerify",     path: "/app/adverify",     icon: "✅" },
      { id: "map",          label: "Map",          path: "/app/map",          icon: "🗺" },
      { id: "brand-intel",  label: "Brand Intel",  path: "/app/brand-intel",  icon: "🛰", phase2: true },
    ],
  },
  account: {
    label: "Account",
    accent: color.slate400,
    items: [
      { id: "billing",  label: "Billing",  path: "/app/billing",  icon: "💳" },
      { id: "settings", label: "Settings", path: "/app/settings", icon: "⚙" },
      { id: "security", label: "Security", path: "/app/security", icon: "🔒" },
      { id: "claim",    label: "Claim Station", path: "/claim",   icon: "📻" },
    ],
  },
};

function rolesForGroup(role) {
  // What sidebar groups does this role see?
  if (role === "admin") return ["station", "agency", "account"];
  if (role === "station_manager") return ["station", "agency", "account"];  // station first, but show agency too — many overlap
  if (role === "advertiser" || role === "brand") return ["agency", "station", "account"];
  return ["account"];
}

// ── Live national pulse — small ticker in the topbar ──────────────────────
function LivePulse() {
  const [pulse, setPulse] = useState(null);
  useEffect(() => {
    const tick = () => api.get("/realtime/pulse").then((r) => setPulse(r.data)).catch(() => {});
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, []);
  if (!pulse) return null;
  // F-112: zero-vs-unknown. If the user has no claimed station AND no
  // meaningful figure comes back, render "—" rather than a hard "0".
  const rawN = pulse.total_listeners ?? pulse.estimated_total_people ?? pulse.people;
  const hasN = rawN != null && rawN > 0;
  const n = hasN ? Number(rawN).toLocaleString() : "—";
  return (
    <div data-testid="topbar-live-pulse" title={`${pulse.online_stations ?? 0} stations online · Ghana`}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px",
        background: SHELL_BG, border: `1px solid ${SHELL_LINE}`, borderRadius: radius.pill }}>
      <Waveform width={20} height={14} bars={4} color={color.green500} tone="dark" />
      <span data-testid="topbar-live-pulse-count"
        style={{ ...type.number, color: hasN ? color.green500 : SHELL_MUTED, fontSize: 13 }}>{n}</span>
      <span style={{ ...type.micro, color: SHELL_MUTED, fontSize: 10 }}>LISTENING NOW</span>
    </div>
  );
}

// ── Station switcher — opens a small dropdown of the user's claimed stations
function StationSwitcher() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [stations, setStations] = useState([]);
  const [active, setActive] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (user?.role !== "station_manager" && user?.role !== "admin") return;
    api.get("/auth/my-stations").then((r) => {
      const list = r.data?.stations || [];
      setStations(list);
      // Persist active station so the dashboard tabs all read the same one
      const saved = localStorage.getItem("ws_active_station_id");
      const found = list.find((s) => s.id === saved) || list[0];
      if (found) {
        setActive(found);
        localStorage.setItem("ws_active_station_id", found.id);
      }
    }).catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  if (!stations.length) return null;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button data-testid="station-switcher" onClick={() => setOpen((v) => !v)}
        style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 12px",
          background: SHELL_BG, border: `1px solid ${SHELL_LINE}`, borderRadius: radius.md,
          color: SHELL_TEXT, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font.sans }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: color.cyan400 }} />
        {active?.name || "Pick a station"}
        <span style={{ color: SHELL_MUTED, fontSize: 10 }}>▾</span>
      </button>
      {open && (
        <div data-testid="station-switcher-menu" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0,
          background: SHELL_SURFACE, border: `1px solid ${SHELL_LINE}`, borderRadius: radius.md,
          boxShadow: elevation.e3, minWidth: 240, padding: 6, zIndex: 60 }}>
          {stations.map((s) => (
            <button key={s.id} data-testid={`station-option-${s.id}`}
              onClick={() => {
                setActive(s);
                localStorage.setItem("ws_active_station_id", s.id);
                setOpen(false);
                // Soft refresh so dashboard re-reads
                window.dispatchEvent(new CustomEvent("ws:station-changed", { detail: s }));
              }}
              style={{ width: "100%", display: "flex", justifyContent: "space-between",
                alignItems: "center", padding: "8px 10px",
                background: active?.id === s.id ? SHELL_BG : "transparent",
                border: "none", color: SHELL_TEXT, fontSize: 13, fontWeight: 500, cursor: "pointer",
                borderRadius: radius.sm, fontFamily: font.sans }}>
              <span>{s.name}</span>
              <span style={{ color: SHELL_MUTED, fontSize: 11, fontFamily: font.mono }}>{s.frequency || ""}</span>
            </button>
          ))}
          <Link to="/claim" data-testid="station-switcher-claim"
            style={{ display: "block", marginTop: 6, padding: "8px 10px", color: color.cyan400,
              fontSize: 12, fontWeight: 600, borderTop: `1px solid ${SHELL_LINE}`, textDecoration: "none" }}>
            + Claim another station
          </Link>
        </div>
      )}
    </div>
  );
}

// ── Avatar menu ────────────────────────────────────────────────────────────
function AvatarMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const initials = (user?.full_name || user?.email || "U").split(/[ @]/)[0].slice(0, 2).toUpperCase();
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button data-testid="topbar-avatar" onClick={() => setOpen((v) => !v)}
        style={{ width: 34, height: 34, borderRadius: "50%", border: `1px solid ${SHELL_LINE}`,
          background: color.audience.station, color: "#fff", fontWeight: 700, fontSize: 12,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {initials}
      </button>
      {open && (
        <div data-testid="avatar-menu" style={{ position: "absolute", top: "calc(100% + 6px)", right: 0,
          background: SHELL_SURFACE, border: `1px solid ${SHELL_LINE}`, borderRadius: radius.md,
          boxShadow: elevation.e3, minWidth: 220, padding: 6, zIndex: 60 }}>
          <div style={{ padding: "8px 10px", borderBottom: `1px solid ${SHELL_LINE}`, color: SHELL_MUTED, fontSize: 11 }}>
            <div style={{ color: SHELL_TEXT, fontWeight: 600, fontSize: 13 }}>{user?.full_name || user?.email}</div>
            <div style={{ ...type.micro, marginTop: 2 }}>{user?.role}</div>
          </div>
          {[
            ["Settings", "/app/settings"],
            ["Security & MFA", "/app/security"],
            ["Billing", "/app/billing"],
          ].map(([lbl, href]) => (
            <button key={href} onClick={() => { setOpen(false); nav(href); }}
              style={{ width: "100%", textAlign: "left", padding: "8px 10px", background: "none",
                border: "none", color: SHELL_TEXT, fontSize: 13, cursor: "pointer", fontFamily: font.sans }}>
              {lbl}
            </button>
          ))}
          <div style={{ borderTop: `1px solid ${SHELL_LINE}`, marginTop: 4, paddingTop: 4 }}>
            <button onClick={() => { logout(); nav("/"); }} data-testid="avatar-logout"
              style={{ width: "100%", textAlign: "left", padding: "8px 10px", background: "none",
                border: "none", color: color.red500, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font.sans }}>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sidebar item ───────────────────────────────────────────────────────────
function SidebarLink({ item, accent }) {
  return (
    <NavLink to={item.path}
      data-testid={`sidebar-nav-${item.id}`}
      style={({ isActive }) => ({
        display: "flex", alignItems: "center", gap: 10,
        padding: "9px 12px", borderRadius: radius.sm, textDecoration: "none",
        color: isActive ? "#fff" : SHELL_MUTED,
        background: isActive ? accent + "22" : "transparent",
        borderLeft: isActive ? `2px solid ${accent}` : "2px solid transparent",
        fontSize: 13, fontWeight: isActive ? 600 : 500,
        transition: transition(["background", "color", "border-color"]),
        fontFamily: font.sans,
      })}>
      <span style={{ fontSize: 14, opacity: 0.9 }}>{item.icon}</span>
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.phase2 && <Pill variant="phase2" data-testid={`sidebar-phase2-${item.id}`}>P2</Pill>}
    </NavLink>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────
function Sidebar({ groups, onItemClick }) {
  return (
    <aside data-testid="app-sidebar" style={{
      width: SIDEBAR_W, flexShrink: 0, background: SHELL_BG,
      borderRight: `1px solid ${SHELL_LINE}`, height: "100vh", position: "sticky", top: 0,
      display: "flex", flexDirection: "column", padding: `${space[4]}px 0`, fontFamily: font.sans,
    }}>
      <div style={{ padding: `0 ${space[5]}px ${space[5]}px`, display: "flex", alignItems: "center", gap: 10 }}>
        <Waveform width={32} height={20} bars={4} color={color.cyan400} tone="dark" />
        <strong style={{ color: SHELL_TEXT, fontSize: 16, fontFamily: font.serif, fontWeight: 700, letterSpacing: "-0.01em" }}>
          WaveScore<span style={{ color: color.green500 }}>Pro</span>
        </strong>
      </div>
      <nav onClick={onItemClick} style={{ flex: 1, overflowY: "auto", padding: `0 ${space[3]}px` }}>
        {groups.map((gid) => {
          const grp = NAV[gid]; if (!grp) return null;
          return (
            <div key={gid} style={{ marginBottom: space[5] }}>
              <div style={{ ...type.micro, color: SHELL_MUTED, padding: `0 ${space[3]}px ${space[2]}px` }}>
                {grp.label}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {grp.items.map((it) => <SidebarLink key={it.id} item={it} accent={grp.accent} />)}
              </div>
            </div>
          );
        })}
      </nav>
      <div style={{ padding: `${space[4]}px ${space[5]}px 0`, borderTop: `1px solid ${SHELL_LINE}`, color: SHELL_MUTED, fontSize: 11, lineHeight: 1.55 }}>
        <Link to="/methodology" style={{ color: SHELL_MUTED, textDecoration: "none" }}>How we measure →</Link>
      </div>
    </aside>
  );
}

// ── Topbar ─────────────────────────────────────────────────────────────────
function Topbar({ onMobileMenuClick }) {
  return (
    <header data-testid="app-topbar" style={{
      height: 56, background: SHELL_SURFACE, borderBottom: `1px solid ${SHELL_LINE}`,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: `0 ${space[5]}px`, fontFamily: font.sans, position: "sticky", top: 0, zIndex: 30,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button data-testid="mobile-menu-toggle" onClick={onMobileMenuClick}
          style={{ background: "none", border: "none", color: SHELL_TEXT, fontSize: 18,
            cursor: "pointer", display: "none", padding: 6 }}
          className="ws-only-mobile">☰</button>
        <StationSwitcher />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <LivePulse />
        <button data-testid="topbar-cmdk" onClick={() => {
          window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, ctrlKey: true }));
        }}
          title="Open command palette (⌘K)"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px",
            background: SHELL_BG, border: `1px solid ${SHELL_LINE}`, borderRadius: radius.sm,
            color: SHELL_MUTED, fontSize: 11, cursor: "pointer", fontFamily: font.mono }}>
          <span>Search</span><kbd style={{ background: SHELL_LINE, color: SHELL_TEXT, padding: "1px 5px", borderRadius: 3, fontSize: 10 }}>⌘K</kbd>
        </button>
        <button data-testid="topbar-notifications" title="Notifications (coming Phase 2)"
          style={{ background: "none", border: `1px solid ${SHELL_LINE}`, color: SHELL_MUTED,
            width: 34, height: 34, borderRadius: radius.sm, cursor: "pointer" }}>🔔</button>
        <AvatarMenu />
      </div>
    </header>
  );
}

// ── AppShell (exported) ────────────────────────────────────────────────────
export function AppShell({ children }) {
  const { user, isLoggedIn } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();

  // Always require auth — Guard in the route handles the redirect to /login.
  const groups = rolesForGroup(user?.role);

  // Close mobile drawer on navigation
  const closeOnNav = useCallback(() => setMobileOpen(false), []);
  useEffect(() => { closeOnNav(); }, [loc.pathname, closeOnNav]);

  if (!isLoggedIn) {
    // Guard sends us to /login; render nothing while that flips.
    return null;
  }

  return (
    <div style={{ background: SHELL_BG, minHeight: "100vh", color: SHELL_TEXT, fontFamily: font.sans }}>
      <style>{`
        .ws-shell-grid { display: grid; grid-template-columns: ${SIDEBAR_W}px 1fr; min-height: 100vh; }
        @media (max-width: 900px) {
          .ws-shell-grid { grid-template-columns: 1fr; }
          .ws-shell-grid > aside { display: ${mobileOpen ? "flex" : "none"}; position: fixed; z-index: 50; width: 280px; }
          .ws-only-mobile { display: inline-block !important; }
        }
      `}</style>
      <div className="ws-shell-grid">
        <Sidebar groups={groups} onItemClick={closeOnNav} />
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Topbar onMobileMenuClick={() => setMobileOpen((v) => !v)} />
          <main data-testid="app-main" style={{
            flex: 1, padding: `${space[6]}px ${space[6]}px ${space[8]}px`,
            background: SHELL_BG, animation: "ws-fade-in 200ms ease-out",
          }}>
            {children ?? <Outlet />}
          </main>
        </div>
      </div>
    </div>
  );
}
